/**
 * Zahlen an den Vektor-Torten (Seite 2 rechts).
 *
 * Weekly 02.10.2026: Ohne Zahlen am Ring ließ sich bei ähnlichen Farbtönen kaum zuordnen,
 * welcher Legendenwert zu welchem Segment gehört. Verglichen wurden vier Varianten; SQUAD
 * hat „Hybrid groß“ gewählt:
 *  - große Segmente: Zahl im Ring (9 pt fett, Loch 45 %),
 *  - kleine Segmente: Zahl in einer Zeile über dem Ring, mit Strich zum Segment,
 *  - der Ring ist so gedreht, dass die kleinen Segmente oben liegen (Kreismittel → 12 Uhr),
 *  - Zahlen ohne Nachkommastellen und ohne „%“; die Legende zeigt nur die Bezeichnungen.
 *
 * Die kleinen Zahlen stehen IMMER oben — auch wenn die Excel nicht sortiert ist und ein
 * kleines Segment unten liegt. Kein Strich schneidet dabei den Ring:
 *  - Austritt in der oberen Ringhälfte → senkrecht nach oben (darüber ist nur Freiraum).
 *  - Austritt in der unteren Hälfte → waagerecht nach außen in eine Gasse neben dem Ring,
 *    dort hoch. Tiefer liegende Segmente nehmen die äußere Gasse, damit sich nichts kreuzt.
 *  - Passt die Zeile nicht, wird die Schrift kleiner (bis 6,5 pt), danach zwei versetzte Reihen
 *    (Kamm: die Striche der oberen Reihe laufen senkrecht durch die Lücken der unteren),
 *    zuletzt wird der Kasten breiter. Überlappen würde nie etwas.
 *  - Zwei Zahlen im Ring, die sich berühren würden: die kleinere wandert nach oben.
 * `none` (Umschalter „ohne Zahlen“) ist der Stand vom 25.09.: Ring ohne Zahlen, Werte in der Legende.
 *
 * Reine Geometrie in pt, damit Vorschau (px) und PDF dieselben Positionen zeichnen.
 */
import { SQUAD_VECTOR_PIE, type SquadPieStyle } from './squadVectorDonut';
import type { SquadPortfolioBlock } from './squadPortfolioSections';

export type SquadPieLabelMode = 'none' | 'ring';

export type SquadDonutLabelOptions = {
  /** Kantenlänge des Rings (77 pt wie bisher). */
  size?: number;
  /** Lochanteil am Außenradius. */
  cutout?: number;
  /** Schriftgröße der Zahlen im Ring. */
  insideFontPt?: number;
  /** Schriftgröße der Zahlen oben, und die Untergrenze beim Verkleinern. */
  outsideFontPt?: number;
  minOutsideFontPt?: number;
  /** Ab diesem Anteil steht die Zahl im Ring (Web: 5 %), sofern sie dort hineinpasst. */
  insideMinShare?: number;
  /** Mindesthöhe über dem Ring — damit beide Torten eines Bereichs auf gleicher Höhe stehen. */
  reserveTopPt?: number;
};

export type SquadDonutGeom = { cx: number; cy: number; outerR: number; innerR: number };

export type SquadDonutLabel = {
  /** Mittelpunkt der Zahl. */
  x: number;
  y: number;
  text: string;
  fontPt: number;
  /** Textfarbe — im Ring je nach Helligkeit des Segments hell oder dunkel. */
  color: string;
  bold: boolean;
  /** Index in `values` — für Tests und Zuordnung. */
  index: number;
  inside: boolean;
};

export type SquadDonutLayout = {
  width: number;
  height: number;
  geom: SquadDonutGeom;
  /** Drehung in rad, 0 = Start bei 12 Uhr, im Uhrzeigersinn. */
  startAngle: number;
  /** Höhe über dem Ring (Zahlenzeile + Abstand); die Legende wird um diesen Wert versetzt. */
  topPt: number;
  labels: SquadDonutLabel[];
  /**
   * Verbindungsstriche als SVG-Pfade (nur gerade Segmente: M … L … L …), in der Reihenfolge
   * der Zahlen außerhalb des Rings in `labels`.
   */
  leaders: string[];
};

export type SquadPieVariant = {
  mode: SquadPieLabelMode;
  options: SquadDonutLabelOptions;
  /** Werte zusätzlich in der Legende (nur ohne Zahlen am Ring). */
  legendValues: boolean;
};

const RING_OPTIONS = Object.freeze({
  insideFontPt: 9,
  outsideFontPt: 8,
  minOutsideFontPt: 6.5,
  cutout: 0.45,
});
const PLAIN: SquadPieVariant = Object.freeze({ mode: 'none', options: Object.freeze({}), legendValues: true });
const RING: SquadPieVariant = Object.freeze({ mode: 'ring', options: RING_OPTIONS, legendValues: false });

/**
 * Darstellung je Stil. Feste Objekte, damit `useMemo` in der Vorschau greift. Seit der
 * SQUAD-Wahl E1 (15.10.2026) ist PLAIN — Ring ohne Zahlen, Werte in der Legende — auch
 * der Rückfall für einen fehlenden oder unbekannten Stil; den Ring mit Zahlen gibt es nur
 * noch auf ausdrückliches 'vector', und das liefert der Mapper nie.
 */
export function squadPieVariant(style: SquadPieStyle | string | undefined): SquadPieVariant {
  return style === 'vector' ? RING : PLAIN;
}

export const SQUAD_DONUT_LABEL_COLOR_DARK = '#0C1C3D';
export const SQUAD_DONUT_LABEL_COLOR_LIGHT = '#FFFFFF';

/**
 * Oberkante der Textbox über dem gewünschten Ziffernmittelpunkt, in em (lineHeight 1).
 * Browser: Grundlinie bei 0,914 em, Ziffernmitte 0,350 em darüber → 0,564.
 * react-pdf setzt die Grundlinie unabhängig von lineHeight bei Oberkante + hhea-Ascent
 * (Segoe UI 2210/2048 = 1,079 em) → 0,729. Gemessen im Review vom 02.10.2026.
 */
export const SQUAD_DONUT_LABEL_TOP_EM_PREVIEW = 0.564;
export const SQUAD_DONUT_LABEL_TOP_EM_PDF = 2210 / 2048 - 0.35;

const FULL_TURN = Math.PI * 2;
const pointAt = (cx: number, cy: number, r: number, angle: number) => ({
  x: cx + r * Math.sin(angle),
  y: cy - r * Math.cos(angle),
});
const fmt = (n: number) => Number(n.toFixed(3)).toString();

/** Ganzzahl; kleine Werte über 0 nicht als „0“, sondern „<1“. */
export function squadDonutLabelNumber(value: number): string {
  if (!(value > 0)) return '0';
  if (value < 0.5) return '<1';
  return String(Math.round(value));
}

/**
 * Breite in pt für Segoe UI, eher zu breit. Gemessen (fontkit): Ziffern 0,539 em, fett 0,575;
 * „<“ 0,684 em, fett 0,707.
 */
export function squadDonutTextWidth(text: string, fontPt: number, bold = false): number {
  let em = 0;
  for (let i = 0; i < text.length; i++) em += text[i] === '<' ? 0.71 : bold ? 0.6 : 0.56;
  return em * fontPt;
}

/** Ziffernhöhe in em (Segoe UI capHeight 0,700) — die Tinte, gegen die Striche geprüft werden. */
export const SQUAD_DONUT_DIGIT_INK_EM = 0.7;

/**
 * Schrift im Ring: Navy oder Weiß, je nachdem, was gegen die Segmentfarbe den höheren
 * Kontrast hat. Gleichstand bei relativer Helligkeit ≈ 0,205 (Navy #0C1C3D ≈ 0,012).
 */
export function squadDonutLabelColorFor(hex: string): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || '').trim());
  if (!m) return SQUAD_DONUT_LABEL_COLOR_LIGHT;
  const n = parseInt(m[1], 16);
  const [r, g, b] = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return luminance > 0.205 ? SQUAD_DONUT_LABEL_COLOR_DARK : SQUAD_DONUT_LABEL_COLOR_LIGHT;
}

type Slice = { index: number; share: number; a0: number; a1: number; mid: number; value: number };

function slicesOf(values: number[], startAngle: number): Slice[] {
  const clean = values.map((v) => (Number.isFinite(Number(v)) ? Math.max(Number(v), 0) : 0));
  const total = clean.reduce((s, v) => s + v, 0);
  if (total <= 0) return [];
  const out: Slice[] = [];
  let angle = startAngle;
  clean.forEach((value, index) => {
    if (value <= 0) return;
    const sweep = (value / total) * FULL_TURN;
    out.push({ index, value, share: value / total, a0: angle, a1: angle + sweep, mid: angle + sweep / 2 });
    angle += sweep;
  });
  return out;
}

/** Abstände der Zahlenzeile (pt). */
const ROW_GAP_TO_RING = 4.5; // Raum für den Knick der Striche zwischen Zeile und Ring
const LABEL_GAP = 3; // Mindestabstand zwischen zwei Zahlen derselben Reihe
const LEADER_CLEAR = 1.5; // Strich der oberen Reihe ↔ Zahl der unteren Reihe
const INSIDE_GAP = 1.5; // Mindestabstand zwischen zwei Zahlen im Ring
const LANE_GAP = 2; // erste Gasse neben dem Ring
const LANE_STEP = 1.6; // jede weitere Gasse
const WIDEN_STEP = 2; // letzte Stufe: Kasten je Seite verbreitern
const MAX_WIDEN = 200;

type RowItem = { index: number; text: string; tw: number; want: number; x: number; lane?: number; row: number };

/**
 * Zahlen verteilen: Reihenfolge nach Wunsch-x bleibt, innerhalb [0, width]. Eine Reihe:
 * Nachbarn mit LABEL_GAP Abstand. Zwei Reihen (Kamm): abwechselnd unten/oben; die Zahl unten
 * hält Abstand zum senkrechten Strich ihrer Nachbarn oben, gleiche Reihe LABEL_GAP.
 * Liefert false, wenn es nicht passt.
 */
function placeRow(items: RowItem[], width: number, twoRows: boolean): boolean {
  items.sort((a, b) => a.want - b.want);
  items.forEach((it, i) => {
    it.row = twoRows ? i % 2 : 0;
  });
  const edge = (it: RowItem) => it.tw / 2 + LABEL_GAP / 2;
  const sameRow = (a: RowItem, b: RowItem) => (a.tw + b.tw) / 2 + LABEL_GAP;
  const next = (a: RowItem, b: RowItem) => (twoRows ? (a.row === 0 ? a.tw : b.tw) / 2 + LEADER_CLEAR : sameRow(a, b));
  // vorwärts: nicht links aus dem Kasten, nicht in die Vorgänger
  items.forEach((it, i) => {
    let x = Math.max(it.want, edge(it));
    if (i >= 1) x = Math.max(x, items[i - 1].x + next(items[i - 1], it));
    if (twoRows && i >= 2) x = Math.max(x, items[i - 2].x + sameRow(items[i - 2], it));
    it.x = x;
  });
  // rückwärts: nicht rechts aus dem Kasten, nicht in die Nachfolger
  for (let i = items.length - 1; i >= 0; i--) {
    const it = items[i];
    let x = Math.min(it.x, width - edge(it));
    if (i + 1 < items.length) x = Math.min(x, items[i + 1].x - next(it, items[i + 1]));
    if (twoRows && i + 2 < items.length) x = Math.min(x, items[i + 2].x - sameRow(it, items[i + 2]));
    it.x = x;
  }
  return items.every((it) => it.x >= edge(it) - 1e-6);
}

/**
 * Lage der Zahlen und Striche für einen Ring. `values` in Legendenreihenfolge, `colors`
 * je Index (für die Schriftfarbe im Ring).
 */
export function squadDonutLabelLayout(
  values: number[],
  colors: string[],
  mode: SquadPieLabelMode,
  options: SquadDonutLabelOptions = {},
): SquadDonutLayout {
  const size = options.size ?? SQUAD_VECTOR_PIE.diameterPt;
  const cutout = options.cutout ?? SQUAD_VECTOR_PIE.cutout;
  const R = size / 2;

  if (mode === 'none') {
    return {
      width: size,
      height: size,
      geom: { cx: R, cy: R, outerR: R, innerR: R * cutout },
      startAngle: 0,
      topPt: 0,
      labels: [],
      leaders: [],
    };
  }

  const insideFont = options.insideFontPt ?? RING_OPTIONS.insideFontPt;
  const outsideFontMax = options.outsideFontPt ?? RING_OPTIONS.outsideFontPt;
  const outsideFontMin = Math.min(options.minOutsideFontPt ?? RING_OPTIONS.minOutsideFontPt, outsideFontMax);
  const insideMinShare = options.insideMinShare ?? 0.05;

  // Welche Segmente sind zu schmal für ihre Zahl? Waagerechter Text hat oben/unten die
  // Bogenlänge zur Verfügung, seitlich nur die Ringdicke — dazwischen gemischt. (Wirkt sich
  // nur bei schmalen Segmenten aus, deren Bogen kürzer als die Ringdicke ist; dort zählt
  // der Bogen — das Ergebnis hängt also nicht von der Drehung ab.)
  const midR = (R + R * cutout) / 2;
  const thickness = R * (1 - cutout);
  const isSmall = (s: Slice) => {
    if (s.share < insideMinShare) return true;
    const arc = s.share * FULL_TURN * midR;
    const room = Math.abs(Math.cos(s.mid)) * arc + Math.abs(Math.sin(s.mid)) * thickness;
    return squadDonutTextWidth(squadDonutLabelNumber(s.value), insideFont, true) > Math.min(room, arc) - 1.5;
  };
  const rotationFor = (small: Set<number>) => {
    if (small.size === 0) return 0;
    const mids = slicesOf(values, 0)
      .filter((s) => small.has(s.index))
      .map((s) => s.mid);
    const sx = mids.reduce((a, m) => a + Math.sin(m), 0);
    const sy = mids.reduce((a, m) => a + Math.cos(m), 0);
    return Math.abs(sx) < 1e-9 && Math.abs(sy) < 1e-9 ? 0 : -Math.atan2(sx, sy);
  };
  // Zahlen im Ring, die sich am gedrehten Ring berühren würden: die kleinere nach oben
  // (eine je Durchgang, danach neu drehen — die Menge wächst nur, das endet sicher).
  const inkH = insideFont * SQUAD_DONUT_DIGIT_INK_EM;
  const crowdedInside = (start: number, small: Set<number>): number | undefined => {
    const boxes = slicesOf(values, start)
      .filter((s) => !small.has(s.index))
      .map((s) => {
        const p = pointAt(0, 0, midR, s.mid);
        return { s, x: p.x, y: p.y, w: squadDonutTextWidth(squadDonutLabelNumber(s.value), insideFont, true) };
      });
    let pick: Slice | undefined;
    for (let i = 0; i < boxes.length; i++) {
      for (let j = i + 1; j < boxes.length; j++) {
        const a = boxes[i];
        const b = boxes[j];
        const apart =
          Math.abs(a.x - b.x) >= (a.w + b.w) / 2 + INSIDE_GAP || Math.abs(a.y - b.y) >= inkH + INSIDE_GAP;
        if (apart) continue;
        const loser = a.s.value < b.s.value || (a.s.value === b.s.value && a.s.index > b.s.index) ? a.s : b.s;
        if (!pick || loser.value < pick.value || (loser.value === pick.value && loser.index > pick.index)) pick = loser;
      }
    }
    return pick?.index;
  };
  const small = new Set(slicesOf(values, 0).filter(isSmall).map((s) => s.index));
  let startAngle = rotationFor(small);
  for (let crowded = crowdedInside(startAngle, small); crowded !== undefined; crowded = crowdedInside(startAngle, small)) {
    small.add(crowded);
    startAngle = rotationFor(small);
  }

  // Gassen nur, wenn ein kleines Segment unten austritt; dann links und rechts gleich breit,
  // damit der Ring mittig bleibt.
  const probe = slicesOf(values, startAngle).filter((s) => small.has(s.index));
  const lower = probe.filter((s) => Math.cos(s.mid) < 0);
  const lanesLeft = lower.filter((s) => Math.sin(s.mid) < 0).length;
  const lanesRight = lower.length - lanesLeft;
  const maxLanes = Math.max(lanesLeft, lanesRight);
  const sidePad = maxLanes > 0 ? LANE_GAP + LANE_STEP * (maxLanes - 1) + 1 : 0;
  let width = size + 2 * sidePad;

  const laneXOf = (cx: number, lane: number) => cx + Math.sign(lane) * (R + LANE_GAP + LANE_STEP * (Math.abs(lane) - 1));
  const makeItems = (font: number, cx: number): RowItem[] => {
    // Gassen: je Seite von oben nach unten nach außen (x hängt nicht von cy ab)
    const laneOf = new Map<number, number>();
    for (const side of [-1, 1]) {
      probe
        .filter((s) => Math.cos(s.mid) < 0 && (side < 0 ? Math.sin(s.mid) < 0 : Math.sin(s.mid) >= 0))
        .sort((a, b) => Math.cos(b.mid) - Math.cos(a.mid))
        .forEach((s, k) => laneOf.set(s.index, side * (k + 1)));
    }
    return probe.map((s) => {
      const text = squadDonutLabelNumber(s.value);
      const lane = laneOf.get(s.index);
      const want = lane === undefined ? pointAt(cx, 0, R + 0.4, s.mid).x : laneXOf(cx, lane);
      return { index: s.index, text, tw: squadDonutTextWidth(text, font), want, x: want, lane, row: 0 };
    });
  };

  // Zahlenzeile(n): Schrift so groß wie möglich, dann zwei Reihen, zuletzt breiter.
  let outsideFont = outsideFontMax;
  let items: RowItem[] = [];
  let rowCount = 0;
  if (small.size > 0) {
    for (let f = outsideFontMax; f >= outsideFontMin - 1e-9 && rowCount === 0; f -= 0.5) {
      const tryItems = makeItems(f, width / 2);
      if (placeRow(tryItems, width, false)) {
        outsideFont = f;
        items = tryItems;
        rowCount = 1;
      }
    }
    if (rowCount === 0) {
      outsideFont = outsideFontMin;
      rowCount = 2;
      const base = width;
      for (let extra = 0; extra <= MAX_WIDEN; extra += WIDEN_STEP) {
        width = base + 2 * extra;
        items = makeItems(outsideFont, width / 2);
        if (placeRow(items, width, true)) break;
      }
    }
  }

  const rowH = outsideFont + 4;
  const naturalTop = rowCount ? rowCount * rowH + ROW_GAP_TO_RING : 0;
  const topPt = Math.max(naturalTop, options.reserveTopPt ?? 0);
  const geom = { cx: width / 2, cy: topPt + R, outerR: R, innerR: R * cutout };
  const slices = slicesOf(values, startAngle);
  const labels: SquadDonutLabel[] = [];
  const leaders: string[] = [];

  for (const s of slices) {
    if (small.has(s.index)) continue;
    const p = pointAt(geom.cx, geom.cy, midR, s.mid);
    labels.push({
      x: p.x,
      y: p.y,
      text: squadDonutLabelNumber(s.value),
      fontPt: insideFont,
      color: squadDonutLabelColorFor(colors[s.index % Math.max(colors.length, 1)]),
      bold: true,
      index: s.index,
      inside: true,
    });
  }

  // Striche: Austritt → (senkrecht | Gasse) bis knapp über den Ring → schräg zur Unterkante
  // der unteren Reihe; die obere Reihe von dort senkrecht weiter. Alle Schrägen liegen im
  // selben Band und behalten die Reihenfolge — so kreuzt sich nichts.
  const knee = topPt - 1.6;
  const centerYOf = (row: number) => topPt - ROW_GAP_TO_RING - rowH / 2 - row * rowH;
  const bottomOf = (row: number) => centerYOf(row) + outsideFont * 0.42;
  for (const it of items) {
    const s = slices.find((x) => x.index === it.index);
    if (!s) continue;
    const exit = pointAt(geom.cx, geom.cy, R + 0.4, s.mid);
    labels.push({
      x: it.x,
      y: centerYOf(it.row),
      text: it.text,
      fontPt: outsideFont,
      color: SQUAD_DONUT_LABEL_COLOR_DARK,
      bold: false,
      index: it.index,
      inside: false,
    });
    const pts =
      it.lane === undefined
        ? [exit, { x: exit.x, y: Math.min(knee, exit.y) }]
        : [exit, { x: laneXOf(geom.cx, it.lane), y: exit.y }, { x: laneXOf(geom.cx, it.lane), y: knee }];
    pts.push({ x: it.x, y: bottomOf(0) });
    if (it.row > 0) pts.push({ x: it.x, y: bottomOf(it.row) });
    leaders.push(pts.map((p, i) => `${i ? 'L' : 'M'} ${fmt(p.x)} ${fmt(p.y)}`).join(' '));
  }

  return { width, height: topPt + size, geom, startAngle, topPt, labels, leaders };
}

/** Höhe über dem Ring, die eine Torte braucht — für die gemeinsame Reserve eines Bereichs. */
export function squadDonutTopPt(values: number[], style: SquadPieStyle | string | undefined): number {
  const variant = squadPieVariant(style);
  return squadDonutLabelLayout(values, [], variant.mode, variant.options).topPt;
}

/** Größte Höhe über dem Ring unter den Torten eines Bereichs (Vektor-Modus). */
export function squadSectionPieReserve(blocks: SquadPortfolioBlock[], pieStyle?: string): number {
  return blocks.reduce(
    (max, b) => (b.kind === 'pie' ? Math.max(max, squadDonutTopPt(b.items.map((i) => Number(i.value)), pieStyle)) : max),
    0,
  );
}
